import type { SalesDocumentSummary } from '@aeris/contracts'

import {
  dateValue,
  numberValue,
  textValue,
  type ExportColumn,
} from './export/table-export.ts'
import { KIND_LABELS } from './sales-labels.ts'
import { STATUS_META } from './sales-status-meta.tsx'

/**
 * O que vai no arquivo quando se exporta Vendas ou Orçamentos — F158.
 *
 * Uma lista, e não o `Record<ColumnId, …>` de `product-export.ts`: aquele
 * existe porque Produtos tem seletor de colunas, e é o mapa entre a coluna da
 * tela e a do arquivo. Vendas tem colunas fixas, então o arquivo é uma lista
 * fixa — inventar o `Record` aqui seria a cerimônia sem o problema.
 *
 * **O arquivo leva mais do que a tela mostra**, de propósito: subtotal,
 * desconto, observação e motivo do cancelamento não cabem numa linha de tabela
 * e são exatamente o que alguém abre a planilha para conferir. O que ele NÃO
 * leva é o que a rota não entregou — a exportação chama a mesma rota da lista,
 * com o mesmo filtro, então o que a permissão esconde continua escondido.
 *
 * Dinheiro sai como está: string decimal do servidor (ADR-0016). `numberValue`
 * faz a vírgula no CSV e o número de verdade no Excel; passar por `Number`
 * aqui seria perder centavo em troca de nada.
 */

type Column = ExportColumn<SalesDocumentSummary>

/** Vazio enquanto é rascunho: o número só nasce quando o documento é firmado. */
const numberOf = (document: SalesDocumentSummary): string =>
  document.number === null ? '' : String(document.number)

export const SALES_EXPORT: readonly Column[] = [
  { id: 'number', label: 'Nº', value: numberOf },
  {
    id: 'kind',
    label: 'Tipo',
    value: (document) => KIND_LABELS[document.kind],
  },
  {
    id: 'status',
    label: 'Situação',
    value: (document) => STATUS_META[document.status].label,
  },
  { id: 'issuedAt', label: 'Emitido em', value: (d) => dateValue(d.issuedAt) },
  { id: 'customerName', label: 'Cliente', value: (d) => d.customerName },
  {
    id: 'salespersonName',
    label: 'Vendedor',
    value: (d) => textValue(d.salespersonName),
  },
  { id: 'branchName', label: 'Loja', value: (d) => d.branchName },
  {
    id: 'subtotalAmount',
    label: 'Subtotal',
    value: (d) => numberValue(d.subtotalAmount),
  },
  {
    id: 'discountAmount',
    label: 'Desconto',
    value: (d) => numberValue(d.discountAmount),
  },
  {
    id: 'totalAmount',
    label: 'Total',
    value: (d) => numberValue(d.totalAmount),
  },
  {
    id: 'validUntil',
    label: 'Vale até',
    value: (d) => dateValue(d.validUntil),
  },
  {
    id: 'courierName',
    label: 'Motoboy',
    value: (d) => textValue(d.courierName),
  },
  {
    id: 'dispatchedAt',
    label: 'Saiu em',
    value: (d) => dateValue(d.dispatchedAt),
  },
  { id: 'notes', label: 'Observação', value: (d) => textValue(d.notes) },
  {
    id: 'cancellationReason',
    label: 'Motivo do cancelamento',
    value: (d) => textValue(d.cancellationReason),
  },
]

/**
 * Vendas leva também como cada uma foi paga (F238). Orçamentos usa a lista de
 * cima: orçamento não tem pagamento, e a coluna sairia vazia.
 *
 * Vazio quando quem exporta não lê pagamentos — a rota não manda.
 */
export const SALES_EXPORT_WITH_PAYMENT: readonly Column[] =
  SALES_EXPORT.flatMap((column) =>
    column.id === 'totalAmount'
      ? [
          column,
          {
            id: 'paymentMethods',
            label: 'Pagamento',
            value: (d: SalesDocumentSummary) =>
              textValue(
                d.paymentMethods === null || d.paymentMethods.length === 0
                  ? null
                  : d.paymentMethods.join(' + ')
              ),
          },
        ]
      : [column]
  )
